import React, { useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { FaGithub, FaExternalLinkAlt, FaEye } from "react-icons/fa";
import "./Projects.css";

const Projects: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [selectedProjects, setSelectedProjects] = useState<{ [key: number]: boolean }>({});

  const projects = [
    {
      id: 1,
      title: "PFB Compra Venta",
      description: "Sistema de compra y venta con gestión de inventario, usuarios y transacciones. Desarrollado con tecnologías modernas para ofrecer una experiencia completa de comercio electrónico.",
      technologies: ["JavaScript", "React", "Node.js", "HTML", "CSS"],
      image: "https://raw.githubusercontent.com/oscaralvarezrua/PFB_Compra_Venta/main/README.md",
      githubUrl: "https://github.com/oscaralvarezrua/PFB_Compra_Venta",
      liveUrl: null,
      category: "E-commerce",
      githubRepo: "oscaralvarezrua/PFB_Compra_Venta",
    },
    {
      id: 2,
      title: "Pokedex",
      description: "Aplicación web interactiva que consume la API de Pokémon para mostrar información detallada de todos los pokémon. Incluye búsqueda, filtros y diseño responsive.",
      technologies: ["JavaScript", "HTML", "CSS", "API REST"],
      image: "https://raw.githubusercontent.com/oscaralvarezrua/Pokedex/main/README.md",
      githubUrl: "https://github.com/oscaralvarezrua/Pokedex",
      liveUrl: null,
      category: "Web App",
      githubRepo: "oscaralvarezrua/Pokedex",
    },
    {
      id: 3,
      title: "Cinema",
      description: "Plataforma de gestión de cine con reservas de películas, gestión de salas y sistema de usuarios. Interfaz intuitiva para administrar un complejo cinematográfico.",
      technologies: ["JavaScript", "React", "Node.js", "HTML", "CSS"],
      image: "https://raw.githubusercontent.com/oscaralvarezrua/Cinema/main/README.md",
      githubUrl: "https://github.com/oscaralvarezrua/Cinema",
      liveUrl: null,
      category: "Management System",
      githubRepo: "oscaralvarezrua/Cinema",
    },
  ];

  const handleProjectClick = (projectId: number) => {
    // Toggle el estado del proyecto específico
    setSelectedProjects((prev) => ({
      ...prev,
      [projectId]: !prev[projectId],
    }));
  };

  // Función para obtener la imagen del proyecto desde GitHub
  const getProjectImage = (project: any) => {
    // Intentar obtener imagen desde GitHub
    const githubImageUrl = `https://raw.githubusercontent.com/${project.githubRepo}/main/screenshot.png`;
    const fallbackImage = `https://via.placeholder.com/400x250/667eea/ffffff?text=${encodeURIComponent(project.title)}`;

    return githubImageUrl;
  };

  return (
    <section id="projects" className="projects">
      <div className="projects-container">
        <motion.div ref={ref} className="projects-content" initial={{ opacity: 0, y: 50 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }}>
          <motion.h2 className="section-title" initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.2 }}>
            Mis Proyectos
          </motion.h2>

          <motion.p className="projects-description" initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.4 }}>
            Una muestra de mis proyectos más destacados, cada uno desarrollado con las mejores prácticas y tecnologías modernas.
          </motion.p>

          <div className="projects-grid">
            {projects.map((project, index) => (
              <motion.div key={project.id} className={`project-card ${selectedProjects[project.id] ? "expanded" : ""}`} initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.2 + index * 0.1 }} whileHover={{ scale: selectedProjects[project.id] ? 1.02 : 1.05 }}>
                <div className="project-image">
                  <img
                    src={getProjectImage(project)}
                    alt={project.title}
                    onError={(e) => {
                      // Si la imagen de GitHub falla, usar placeholder
                      const target = e.target as HTMLImageElement;
                      target.src = `https://via.placeholder.com/400x250/667eea/ffffff?text=${encodeURIComponent(project.title)}`;
                    }}
                  />
                  <div className="project-overlay">
                    <button className="project-btn" onClick={() => handleProjectClick(project.id)}>
                      <FaEye />
                      Ver Detalles
                    </button>
                  </div>
                </div>

                <div className="project-content">
                  <div className="project-header">
                    <h3>{project.title}</h3>
                    <span className="project-category">{project.category}</span>
                  </div>

                  <p className="project-description">{project.description}</p>

                  <div className="project-technologies">
                    {project.technologies.map((tech, techIndex) => (
                      <span key={techIndex} className="tech-tag">
                        {tech}
                      </span>
                    ))}
                  </div>

                  {selectedProjects[project.id] && (
                    <motion.div className="project-details" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }}>
                      <div className="details-content">
                        <h4>Características Principales</h4>
                        <ul>
                          <li>Interfaz de usuario moderna y responsive</li>
                          <li>Arquitectura escalable y mantenible</li>
                          <li>Implementación de mejores prácticas de desarrollo</li>
                          <li>Optimización para rendimiento y accesibilidad</li>
                        </ul>

                        <h4>Tecnologías Utilizadas</h4>
                        <div className="tech-details">
                          {project.technologies.map((tech, techIndex) => (
                            <span key={techIndex} className="tech-detail">
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}

                  <div className="project-links">
                    <a href={project.githubUrl} target="_blank" rel="noopener noreferrer" className="project-link" aria-label={`Ver código de ${project.title} en GitHub`}>
                      <FaGithub />
                      Código
                    </a>
                    {project.liveUrl && (
                      <a href={project.liveUrl} target="_blank" rel="noopener noreferrer" className="project-link" aria-label={`Ver demo en vivo de ${project.title}`}>
                        <FaExternalLinkAlt />
                        Demo
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div className="projects-cta" initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.8 }}>
            <p>¿Te interesa alguno de mis proyectos?</p>
            <button className="btn btn-primary" onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}>
              Contáctame
            </button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;
